// Site configuration and the three-level resolution of rules — chapter 6 and the owner's
// direction (2026-10-28): site defaults on the settings page; rules about goods in the
// handling profile assigned per category or per article; facts about a supplier's labels
// on the supplier link. Resolution: site → profile → supplier. Pure: no I/O, shared by the
// server (validation, bundle) and the client (what the phone asks for).
import { z } from "zod";
import { AttributeRequirement, HandlingProfileConfig } from "./handling-profile.js";
export const ReasonCode = z.object({ code: z.string().trim().min(1).max(40), label: z.string().trim().min(1).max(80) });
/** What the site settings page holds (admin mockup v1 §10). Every field has a default. */
export const SiteConfig = z
    .object({
    profiles: z
        .object({
        /** May the division's rules and the articles point out another profile than the site's
         *  default? Owner 2026-10-30: locked by default on every level. */
        allowBelow: z.boolean().default(false),
    })
        .default({}),
    lots: z
        .object({
        /** Owner 2026-10-30: at Kalundgren a batch number names one carton, so the same number
         *  twice for a product is an error — Trail checks it (receiving refuses a repeat, the
         *  ERP mirror records a deviation when the ERP holds duplicates; Visma has no such lock). */
        uniquePerProduct: z.boolean().default(false),
    })
        .default({}),
    receiving: z
        .object({
        /** Location (id) received goods stand at until put-away; must carry the receiving role. */
        locationId: z.string().nullable().default(null),
        /** Flow ids the site has enabled on the receiving menu. */
        enabledFlows: z.array(z.string().min(1)).default(["receive-pallet"]),
        afterPallet: z.enum(["next-pallet", "summary"]).default("next-pallet"),
        blindReceipt: z.boolean().default(false),
        /** An identity already in stock scanned again at receiving (§4.5): refuse, count it
         *  as a new container, or ask for a serial that tells the two apart. */
        collision: z.enum(["block", "count", "ask-serial"]).default("block"),
        /** With "count": the repeat is fine only when the container in stock was received the
         *  same day (one delivery), or on any day (owner 2026-10-28: configurable). */
        collisionSameDayOnly: z.boolean().default(false),
        /** With "ask-serial": what happens when no serial can be given (profile forbids serials,
         *  or the operator declines): refuse the container, or count it anyway. */
        collisionEscape: z.enum(["reject", "count"]).default("reject"),
        /** A required attribute missing on the label (today: best-before): the operator must
         *  enter it, or may receive without it with a deviation recorded. */
        missingRequired: z.enum(["must-enter", "allow-without"]).default("must-enter"),
        /** A scanned article that is not on the purchase order (owner 2026-11-02: must not be possible):
         *  refused, accepted with a deviation, or accepted quietly. Blind receipts have no lines to check. */
        outsideOrder: z.enum(["refuse", "warn", "allow"]).default("refuse"),
        /** The pallet's last scan may be a STORAGE location instead of the receiving area (owner 2026-11-02):
         *  received and put away in one go — the goods are received at the receiving area for the record and
         *  moved to the bin in the same batch; holds, pallet capacity and storage class apply. */
        directToStorage: z.boolean().default(true),
    })
        .default({}),
    reasons: z
        .object({
        short: z.array(ReasonCode).default([
            { code: "backorder", label: "Restnoterat" },
            { code: "short-noted", label: "Saknas enl. fraktsedel" },
            { code: "damaged", label: "Skadad – nekad" },
        ]),
        extra: z.array(ReasonCode).default([
            { code: "wrong-item", label: "Fel artikel" },
            { code: "substitute", label: "Ersättningsvara" },
            { code: "over", label: "Överleverans" },
        ]),
        /** Why a container went somewhere else than the put-away proposal (enforcement "reason"). */
        override: z.array(ReasonCode).default([
            { code: "full", label: "Platsen var full" },
            { code: "blocked", label: "Platsen blockerad eller trasig" },
            { code: "better", label: "Bättre plats i närheten" },
        ]),
        /** Where a date entered by hand was read from (configured list, owner 2026-10-28). */
        dateSources: z.array(ReasonCode).default([
            { code: "package", label: "Förpackningen" },
            { code: "delivery-note", label: "Följesedeln" },
            { code: "supplier", label: "Leverantörens uppgift" },
        ]),
    })
        .default({}),
    /** Name of the published handling profile that applies when no rule or article says otherwise. */
    defaultProfile: z.string().trim().min(1).default("Standard"),
    move: z
        .object({
        /** Roles that see Flytta on the menu (owner 2026-10-28: a role setting; all roles here). */
        roles: z.array(z.string()).default(["operator", "lead", "quality", "administrator"]),
    })
        .default({}),
    /** Registering without a scan — receiving and picking by hand with a comment (owner 2026-11-01:
     *  who may is a setting; everyone at Kalundgren, not everywhere). */
    manual: z
        .object({
        roles: z.array(z.string()).default(["operator", "lead", "quality", "administrator"]),
        /** Per flow (owner 2026-11-01): off, allowed with a comment for the roles above, or leads only.
         *  Every manual registration is a deviation for the lead (`manual-entry`). */
        receive: z.enum(["off", "comment", "lead"]).default("comment"),
        pick: z.enum(["off", "comment", "lead"]).default("comment"),
    })
        .default({}),
    putAway: z
        .object({
        /** How the put-away proposal is chosen (§4.2.4, D4-4 enforcement *free* for now):
         *  the nearest empty storage bin; the empty bin nearest to where the same product
         *  already stands (consolidation); or the product's fixed bin first, then the same. */
        strategy: z.enum(["nearest-empty", "near-same-product", "fixed-bin"]).default("nearest-empty"),
        /** D4-4: a different place than proposed is just recorded (free) or needs a reason first. */
        enforcement: z.enum(["free", "reason"]).default("free"),
    })
        .default({}),
    picking: z
        .object({
        /** The identity of a piece cut out of a container (owner 2026-10-28: no serials here —
         *  a suffix on the lot number keeps the barcode as it is; serial for sites that want it). */
        splitIdentity: z.enum(["lot-suffix", "serial"]).default("lot-suffix"),
        /** Several orders may share an outbound pallet (owner 2026-10-28: allowed, configurable). */
        combinedPallets: z.boolean().default(true),
        /** After an order is done (owner 2026-11-01): back to the Utleverans menu, or straight to the next order. */
        afterOrder: z.enum(["menu", "next"]).default("menu"),
        /** Where a closed outbound pallet goes (owner 2026-11-02: picked goods must leave the storage
         *  location at once, or the place looks taken and the stock mirror counts them as still there).
         *  A location with the ship, pack or staging role; null = the pallet keeps its place. */
        outboundLocationId: z.string().nullable().default(null),
        /** Who picks what (owner 2026-11-01, on the site): work queues with a rule each over the order's
         *  fields, first match wins, the default queue takes the rest; users and devices subscribe. */
        queues: z.array(z.object({ name: z.string().trim().min(1).max(40), rule: z.string().max(1000).default(""), default: z.boolean().default(false) })).max(50).default([]),
        /** When the outbound pallet's label prints (owner 2026-10-29: when the pallet is done, not
         *  when it is begun): at "Pall klar" with the content, at "Ny pall" with the SSCC only, both, or never. */
        palletLabel: z.enum(["none", "end", "start", "both"]).default("end"),
        /** Storage classes or packaging words that go at the bottom of a pallet: lines with such
         *  products are offered first. */
        bottomFirst: z.array(z.string()).default([]),
        /** How the pick list is arranged (owner 2026-10-29: configurable; Kalundgren groups per
         *  carrier): "flat" in pick order, or "carrier" with one group per carrier, the carrier
         *  that collects earliest first. */
        queue: z.enum(["flat", "carrier"]).default("flat"),
        /** The carriers that collect, with their normal pickup time (HH:MM). Orders are grouped
         *  under these; an order whose carrier is not listed goes last. The ERP adapter names the
         *  carrier on the order; the times are the site's own knowledge. */
        carriers: z.array(z.object({ name: z.string().trim().min(1).max(80), pickupTime: z.string().regex(/^\d{2}:\d{2}$/).nullable().default(null) })).default([]),
    })
        .default({}),
    layout: z
        .object({
        /** How a rack component names its bins (owner 2026-10-30: Kalundgren = aisle, bay between
         *  uprights, shelf level from the floor, compartment from the left). */
        binCode: z.string().trim().min(1).max(80).default("{aisle}-{bay}-{level}-{compartment}"),
        /** The pallet place the components are sized from, millimetres. */
        palletLong: z.number().int().min(100).max(5000).default(1200),
        palletShort: z.number().int().min(100).max(5000).default(800),
        margin: z.number().int().min(0).max(1000).default(100),
        /** The racks' total height, millimetres (owner 2026-10-31: all racks are equally tall; levels
         *  are spread evenly over it until heights per level come). A rack may state its own. */
        rackHeight: z.number().int().min(500).max(20000).default(5000),
        /** Beam length between two uprights, mm; null = computed from the compartments: pallets ×
         *  the side facing the aisle + a margin on each side of each pallet (EN 15620: ≥ 75 mm below
         *  3 m, ≥ 100 mm above; 100 is the usual figure). Owner 2026-10-31: a fixed 2,700 made HA
         *  14.1 m where two 800 pallets per bay give 1,900 and the drawing's 10.1 m. */
        beamLength: z.number().int().min(500).max(10000).nullable().default(null),
        /** Rack types (owner 2026-10-31): templates a rack component points out — what a compartment
         *  holds, how a bay's width comes about (computed from pallets, or fixed like Skafferiet's 1 m
         *  shelves), depth, post, height and levels with optional heights per level. */
        rackTypes: z
            .array(z.object({
            id: z.string().trim().min(1).max(40),
            name: z.string().trim().min(1).max(80),
            holds: z.enum(["pallet", "carton"]).default("pallet"),
            bayWidth: z.enum(["computed", "fixed"]).default("computed"),
            width: z.number().int().min(300).max(10000).default(1000),
            compartments: z.number().int().min(1).max(20).default(2),
            /** null = from the pallet orientation (pallet racks). */
            depth: z.number().int().min(200).max(3000).nullable().default(null),
            /** The width a post adds between two bays, mm (shelves that sit into the posts add little: Skafferiet ≈ 6). */
            post: z.number().int().min(0).max(300).default(100),
            height: z.number().int().min(500).max(20000).default(5000),
            levels: z.number().int().min(1).max(20).default(3),
            /** Height of each level from the floor up, mm; null = spread evenly. */
            levelHeights: z.array(z.number().int().min(100).max(10000)).max(20).nullable().default(null),
            /** Pallets one compartment takes (owner 2026-11-02: two pallets cannot share a pallet-rack compartment):
             *  null = 1 for pallet racks and no limit for shelving; 0 = no limit. Publishing writes it on the aisle. */
            palletsPerCompartment: z.number().int().min(0).max(99).nullable().default(null),
        }))
            .default([
            { id: "pallet-eur", name: "Pallställ EUR", holds: "pallet", bayWidth: "computed", width: 2700, compartments: 2, depth: null, post: 100, height: 5000, levels: 3, levelHeights: null },
            { id: "shelf", name: "Hyllställ", holds: "carton", bayWidth: "fixed", width: 1000, compartments: 3, depth: 600, post: 50, height: 2000, levels: 5, levelHeights: null },
        ]),
    })
        .default({}),
    locations: z
        .object({
        /** Storage classes with their temperature range (owner 2026-10-31: a location's temperature
         *  must be set in admin). Set on a zone, aisle or rack and inherited by every bin below; a
         *  product's storage requirement is matched against it. */
        storageClasses: z
            .array(z.object({ key: z.string().trim().min(1).max(40), name: z.string().trim().min(1).max(80), minC: z.number().nullable().default(null), maxC: z.number().nullable().default(null) }))
            .default([
            { key: "ambient", name: "Rumstemperatur", minC: 15, maxC: 25 },
            { key: "chilled", name: "Kyl", minC: 2, maxC: 8 },
            { key: "frozen", name: "Frys", minC: null, maxC: -18 },
        ]),
        /** What a put-away or move onto a location of another class than the product needs does. */
        storageCheck: z.enum(["off", "warn", "refuse"]).default("warn"),
    })
        .default({}),
    counting: z
        .object({
        /** What is asked for besides the scan (owner 2026-10-30): nothing, the quantity of split
         *  containers only, or the quantity of every container. */
        quantity: z.enum(["none", "split", "all"]).default("split"),
        /** May an operator count the single location in front of them without a round? */
        adHoc: z.boolean().default(true),
        /** Zero-point at pick: when the last known container leaves a location the picker confirms
         *  it is empty; off, on everywhere, or on storage locations only. */
        zeroPointAtPick: z.enum(["off", "on", "storage-only"]).default("on"),
        /** Write the count date to the ERP where it has a column for it (Visma: per batch row). */
        erpCountDate: z.boolean().default(true),
    })
        .default({}),
    printing: z
        .object({
        /** When Trail prints the pallet label (owner 2026-10-28): at pallet end with the content,
         *  at pallet start with the SSCC only, both, or never. */
        palletLabel: z.enum(["none", "end", "start", "both"]).default("none"),
        /** Printer role the receiving flow prints to (R7.20). */
        printerRole: z.string().trim().min(1).default("receiving"),
        /** Print places (owner 2026-11-02: labels and documents print in several places; the places
         *  are the site's to name and extend). A printer stands at one or more; automations and
         *  buttons choose a printer by place. The three built-in keys stay (flows point at them). */
        places: z.array(z.object({ key: z.string().trim().min(1).max(40), name: z.string().trim().min(1).max(60) })).default([{ key: "receiving", name: "Mottagning" }, { key: "packing", name: "Packbord" }, { key: "office", name: "Kontor" }]),
        /** Pallet label shape: landscape 102 × 220 mm on the pallet base (owner 2026-10-28), or
         *  the GS1 logistic-label portrait on 102 × 152 mm. Full template design is a later item (R3.8). */
        palletLabelTemplate: z.enum(["landscape-102x220", "portrait-102x152"]).default("landscape-102x220"),
    })
        .default({}),
})
    .strict();
export const BUILTIN_PRINT_PLACES = ["receiving", "packing", "office"];
/** Per-supplier facts that override the profile's capture rules for that supplier's labels. */
export const CaptureOverrides = z
    .object({
    lot: AttributeRequirement.optional(),
    bestBefore: AttributeRequirement.optional(),
    expiry: AttributeRequirement.optional(),
    productionDate: AttributeRequirement.optional(),
    serial: AttributeRequirement.optional(),
    netContent: AttributeRequirement.optional(),
    partialCodes: z.enum(["assemble-required", "accept-alone", "reject"]).optional(),
})
    .strict();
/** Facts about one supplier's labels that hold for all their articles unless the supplier link
 *  for an article says otherwise (admin mockup v2 §3; owner 2026-10-28: settings at supplier level). */
export const SupplierLabelFacts = z
    .object({
    captureOverrides: CaptureOverrides.default({}),
    /** Where the content sits in their codes, unless the link says otherwise. */
    quantityFrom: z.object({ ai: z.string().regex(/^\d{2,4}$/), uom: z.string().min(2).max(3) }).nullable().default(null),
    /** Free text: symbology and layout, e.g. "GS1-128 · två koder per etikett". */
    barcode: z.string().trim().max(120).nullable().default(null),
    /** Free text: how their lot numbers are built. */
    lotGrammar: z.string().trim().max(200).nullable().default(null),
    /** GS1 company prefixes seen on their labels. */
    knownPrefixes: z.array(z.string().regex(/^\d{4,12}$/)).default([]),
})
    .strict();
/** The rules for one product (owner 2026-10-30): company default → site default → the company's
 *  chosen dimension's rule for the product's value → the article's own profile, each level able to
 *  lock the ones below; then the supplier's label facts override what the label carries. One function
 *  for the phone and the server, so a product is "bulk" in the same cases everywhere. */
export function rulesForProduct(ctx, product, supplier) {
    const byName = (name) => (name ? ctx.profiles.find((p) => p.name === name) ?? null : null);
    const tc = ctx.tenantProfiles;
    // The chain of dimensions, in the company's order (owner 2026-11-03: Gr, then the product category). A
    // value without a rule passes the decision on; a rule with "lower levels may change = no" locks what follows.
    const chain = tc?.dimensions?.length ? tc.dimensions : tc?.dimension ? [tc.dimension] : ["category"];
    const dimensionRules = chain.map((dimension) => {
        const value = product ? (product.classifications?.[dimension] ?? (dimension === "category" ? product.category ?? null : null)) : null;
        const rule = value ? ctx.rules.find((r) => (r.dimension ?? "category") === dimension && r.category === value) ?? null : null;
        return rule ? { ref: byName(rule.profileName), allowBelow: rule.allowArticleOverride ?? false, dimension } : { ref: null, allowBelow: true, dimension };
    });
    return resolveRules({
        company: { ref: byName(tc?.defaultProfile), allowBelow: tc?.allowBelow ?? false },
        site: { ref: byName(ctx.siteProfiles?.defaultProfile), allowBelow: ctx.siteProfiles?.allowBelow ?? false },
        dimensionRules,
        articleProfile: byName(product?.handlingProfileName),
        supplier,
        fallback: ctx.fallback,
    });
}
/** Bulk handling (profile identification.unit = "bulk"): a quantity with no identity. */
export const isBulk = (rules) => rules.identification.unit === "bulk";
/** Walk company → site → the chosen division's rule → article: the most specific level that
 *  was allowed to decide names the profile. A level with a profile and allowBelow=false ends
 *  the walk; a level without a profile is skipped. Supplier label facts then override what the
 *  label carries (not the requirements). */
export function resolveRules(input) {
    const site = "ref" in input.site ? input.site : { ref: input.site, allowBelow: true };
    // Several dimensions in order (owner 2026-11-03), or the older single rule.
    const dims = input.dimensionRules ?? (input.dimensionRule ? [input.dimensionRule] : []);
    const levels = [
        { from: "company", level: input.company ?? { ref: null, allowBelow: true }, dimension: null },
        { from: "site", level: site, dimension: null },
        ...dims.map((d) => ({ from: "dimension", level: d, dimension: d.dimension ?? null })),
        { from: "article", level: { ref: input.articleProfile, allowBelow: true }, dimension: null },
    ];
    let chosen = null;
    let lockedBy = null;
    for (const { from, level, dimension } of levels) {
        if (!level.ref)
            continue;
        chosen = { ref: level.ref, from, dimension };
        if (!level.allowBelow && from !== "article") {
            lockedBy = from;
            break;
        }
    }
    if (!chosen) {
        const ref = input.fallback ?? (site.ref ?? { name: "Standard", version: 0, config: HandlingProfileConfig.parse({}) });
        chosen = { ref, from: "site", dimension: null };
    }
    const capture = { ...chosen.ref.config.capture };
    const overridden = [];
    if (input.supplier) {
        for (const [k, v] of Object.entries(input.supplier)) {
            if (v === undefined)
                continue;
            capture[k] = v;
            overridden.push(k);
        }
    }
    return { profileName: chosen.ref.name, profileVersion: chosen.ref.version, profileFrom: chosen.from, dimension: chosen.dimension, lockedBy, capture, identification: chosen.ref.config.identification, storage: chosen.ref.config.storage, supplierOverrides: overridden };
}
/** Whether a product belongs at the bottom of an outbound pallet, from the site's
 *  `picking.bottomFirst` words: a word matches the product's category, its handling profile
 *  or a word in its name, case-insensitively. Lines with such products are picked first. */
export function isBottomFirst(product, words) {
    if (words.length === 0)
        return false;
    const ws = words.map((w) => w.trim().toLowerCase()).filter(Boolean);
    const fields = [product.category, product.handlingProfileName].map((f) => (f ?? "").trim().toLowerCase());
    const nameWords = product.name.toLowerCase().split(/[^\p{L}\p{N}]+/u);
    return ws.some((w) => fields.includes(w) || nameWords.includes(w));
}
//# sourceMappingURL=site-config.js.map